import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Plus, Coins, MessageCircle } from 'lucide-react';

const formatUnreadCount = (count) => (count > 99 ? '99+' : String(count));

const Header = ({ onCreditsClick, onProfileClick, onMessagesClick, onTicketClick, unreadMessages = 0, ticketCount = 0 }) => {
  const { user } = useAuth();
  const safeUnreadMessages = Math.max(0, Number(unreadMessages) || 0);
  const safeTicketCount = Math.max(0, Number(ticketCount) || 0);

  return (
    <motion.header
      className="webview-safe-top sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/40 px-5 py-4 flex justify-between items-center"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 24 }}
    >
      {/* Logo */}
      <div className="flex items-center gap-2">
        <h1 className="text-3xl font-bold font-['Outfit'] text-gradient tracking-tight">
          Harday
        </h1>
      </div>

      {/* Right side: Credits + Messages + Profile */}
      <div className="flex items-center gap-2.5">
        {/* Credits Capsule */}
        <motion.button
          onClick={onCreditsClick}
          data-testid="credits-capsule"
          className="glass-panel px-4 py-2 rounded-full flex items-center gap-2"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Coins className="w-4 h-4 text-[#FBBF24]" />
          <span className="text-base font-semibold text-[#000000]">
            {user?.credits || 0}
          </span>
          <Plus className="w-4 h-4 text-[#9333EA]" />
        </motion.button>

        {/* Ticket button — shows "T" with 0 tickets, otherwise the green count */}
        <motion.button
          type="button"
          onClick={onTicketClick}
          data-testid="ticket-btn"
          aria-label={safeTicketCount > 0 ? `${safeTicketCount} tickets` : 'View your tickets'}
          className="relative h-10 w-10 shrink-0 rounded-full bg-white/85 shadow-[0_10px_28px_rgba(147,51,234,0.16)] border border-white/70 flex items-center justify-center overflow-visible"
          whileHover={{ scale: 1.06, rotate: -2 }}
          whileTap={{ scale: 0.94 }}
        >
          <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#9333EA]/12 via-white/0 to-[#22C55E]/14" />
          {safeTicketCount > 0 ? (
            <span
              data-testid="ticket-count"
              className="relative text-lg font-bold text-[#16A34A] font-['Outfit'] leading-none"
            >
              {safeTicketCount}
            </span>
          ) : (
            <span
              data-testid="ticket-letter"
              className="relative text-lg font-bold text-[#9333EA] font-['Outfit'] leading-none"
            >
              T
            </span>
          )}
        </motion.button>

        <motion.button
          type="button"
          onClick={onMessagesClick}
          data-testid="messages-btn"
          aria-label={safeUnreadMessages > 0 ? `${safeUnreadMessages} unread messages` : 'Open messages'}
          className="relative h-10 w-10 shrink-0 rounded-full bg-white/85 shadow-[0_10px_28px_rgba(147,51,234,0.16)] border border-white/70 flex items-center justify-center overflow-visible"
          whileHover={{ scale: 1.06, rotate: -2 }}
          whileTap={{ scale: 0.94 }}
        >
          <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#9333EA]/12 via-white/0 to-[#EC4899]/14" />
          <MessageCircle className="relative w-5 h-5 text-[#9333EA]" strokeWidth={2.4} />
          {safeUnreadMessages > 0 && (
            <span
              data-testid="messages-unread-badge"
              className="absolute -right-1.5 -top-1.5 min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-gradient-to-r from-[#F97316] to-[#EC4899] text-white text-[10px] leading-none font-bold flex items-center justify-center ring-2 ring-white shadow-lg"
            >
              {formatUnreadCount(safeUnreadMessages)}
            </span>
          )}
        </motion.button>

        {/* Profile Picture with Pulsating Halo */}
        <motion.button
          onClick={onProfileClick}
          data-testid="profile-picture-trigger"
          className="relative"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <div className="pulse-halo w-10 h-10 rounded-full overflow-hidden border-2 border-white">
            <img
              src={user?.profilePic || 'https://images.unsplash.com/photo-1623594675959-02360202d4d6?crop=entropy&cs=srgb&fm=jpg&w=150&q=85'}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.button>
      </div>
    </motion.header>
  );
};

export default Header;
